import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Workflow, Plus, Pencil, Trash2, Mail, Building2, ShieldCheck, Home, BellRing } from "lucide-react";
import { toast } from "sonner";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function ApprovalRouting() {
  const [routes, setRoutes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, name, email, classes }
  const [saving, setSaving] = useState(false);
  const [reminderCfg, setReminderCfg] = useState({ enabled: true, hours: 48, repeat_daily: false });
  const [savingReminders, setSavingReminders] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, d, u, rs] = await Promise.all([
        api.get("/approval-routing"),
        api.get("/departments").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/approval-routing/settings").catch(() => ({ data: null })),
      ]);
      setRoutes(r.data || []);
      setDepartments(d.data || []);
      setUsers(u.data || []);
      if (rs.data) setReminderCfg(rs.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load approval routing");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveReminders = async () => {
    const h = Number(reminderCfg.hours);
    if (!h || h < 1 || h > 720) return toast.error("Reminder window must be between 1 and 720 hours");
    setSavingReminders(true);
    try {
      const { data } = await api.put("/approval-routing/settings", { ...reminderCfg, hours: h });
      setReminderCfg(data);
      toast.success("Reminder settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingReminders(false); }
  };

  const classLabel = useMemo(() => {
    const m = { cmo: "Admin / CMO", housing: "Housing" };
    departments.forEach((d) => { m[`department:${d.key}`] = d.name || d.key; });
    return (c) => m[c] || c.replace("department:", "");
  }, [departments]);

  const toggleClass = (c) => setEditing((p) => ({
    ...p,
    classes: p.classes.includes(c) ? p.classes.filter((x) => x !== c) : [...p.classes, c],
  }));

  const save = async () => {
    const name = (editing.name || "").trim();
    const email = (editing.email || "").trim();
    if (!name) return toast.error("Name is required");
    if (!EMAIL_RE.test(email)) return toast.error("Enter a valid email address");
    if (editing.classes.length === 0) return toast.error("Assign at least one approval class");
    setSaving(true);
    try {
      const payload = { name, email, classes: editing.classes };
      if (editing.id) await api.put(`/approval-routing/${editing.id}`, payload);
      else await api.post("/approval-routing", payload);
      toast.success(editing.id ? "Approver updated" : "Approver added — they'll now receive approval emails");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.name} from approval routing? They will stop receiving approval emails.`)) return;
    try {
      await api.delete(`/approval-routing/${r.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const pickUser = (email) => {
    const u = users.find((x) => x.email === email);
    setEditing((p) => ({ ...p, email, name: p.name || (u ? u.name : "") }));
  };

  return (
    <div className="space-y-6 fade-in" data-testid="approval-routing-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Workflow className="h-8 w-8 text-[#40916C]" /> Approval routing
          </h1>
          <p className="text-[#525C58] mt-1 max-w-2xl">
            Assign approval classes to people. Whenever a visit request enters their stage,
            they automatically receive an email with the visitor details and a review link.
            One person can hold several classes.
          </p>
        </div>
        <Button onClick={() => setEditing({ name: "", email: "", classes: [] })}
                className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="add-approver-btn">
          <Plus className="h-4 w-4 mr-1" /> Add approver
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="routing-stage-cards">
        <Card className="border-[#E8E8E6]"><CardContent className="py-4 flex gap-3 items-start">
          <Building2 className="h-5 w-5 text-[#40916C] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Department approvals</p>
            <p className="text-[11px] text-[#7A8A84]">Fires when a new request is submitted (or reassigned) for that department.</p>
          </div>
        </CardContent></Card>
        <Card className="border-[#E8E8E6]"><CardContent className="py-4 flex gap-3 items-start">
          <ShieldCheck className="h-5 w-5 text-[#40916C] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Admin / CMO approvals</p>
            <p className="text-[11px] text-[#7A8A84]">Fires when a request passes department review and awaits the CMO / admin step.</p>
          </div>
        </CardContent></Card>
        <Card className="border-[#E8E8E6]"><CardContent className="py-4 flex gap-3 items-start">
          <Home className="h-5 w-5 text-[#40916C] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Housing approvals</p>
            <p className="text-[11px] text-[#7A8A84]">Fires when the CMO approves and housing needs to be finalised.</p>
          </div>
        </CardContent></Card>
      </div>

      <Card className="border-[#E8E8E6]" data-testid="reminder-settings-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BellRing className="h-4 w-4 text-[#40916C]" /> Reminder nudges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={reminderCfg.enabled}
                     onChange={(e) => setReminderCfg({ ...reminderCfg, enabled: e.target.checked })}
                     data-testid="reminder-enabled-toggle" />
              Send reminders
            </label>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[#525C58]">after waiting</span>
              <Input type="number" min={1} max={720} value={reminderCfg.hours}
                     onChange={(e) => setReminderCfg({ ...reminderCfg, hours: e.target.value })}
                     className="w-20 h-8" disabled={!reminderCfg.enabled}
                     data-testid="reminder-hours-input" />
              <span className="text-[#525C58]">hours</span>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={reminderCfg.repeat_daily}
                     onChange={(e) => setReminderCfg({ ...reminderCfg, repeat_daily: e.target.checked })}
                     disabled={!reminderCfg.enabled}
                     data-testid="reminder-repeat-toggle" />
              Repeat daily until acted on
            </label>
            <Button size="sm" onClick={saveReminders} disabled={savingReminders}
                    className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="reminder-save-btn">
              {savingReminders ? "Saving…" : "Save"}
            </Button>
          </div>
          <p className="text-[11px] text-[#7A8A84] mt-2">
            Routed approvers get a nudge email when a request has been waiting at their stage longer than this window.
            With daily repeat off, each stage nudges only once.
          </p>
        </CardContent>
      </Card>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-base">{routes.length} routed approver{routes.length === 1 ? "" : "s"}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-[#7A8A84] text-center py-8">Loading…</p>
          ) : routes.length === 0 ? (
            <div className="text-center py-12 text-[#7A8A84]" data-testid="routing-empty-state">
              <Workflow className="h-10 w-10 mx-auto text-[#B8C2BC] mb-2" />
              <p>No approvers routed yet.</p>
              <p className="text-[11px] mt-1">Add someone (e.g. Head of Medicine → Medicine + Pediatrics) to start sending approval emails automatically.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="routing-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Approval classes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {routes.map((r) => (
                    <TableRow key={r.id} data-testid={`routing-row-${r.id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-[#525C58]"><span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {r.email}</span></TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(r.classes || []).map((c) => (
                            <Badge key={c} variant="outline" className="border-[#1B4332]/20 bg-[#F0F5F2] text-[#1B4332] text-[10px]">
                              {classLabel(c)}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => setEditing({ id: r.id, name: r.name, email: r.email, classes: [...(r.classes || [])] })} data-testid={`edit-route-${r.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => remove(r)} data-testid={`delete-route-${r.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit approver" : "Add approver"}</DialogTitle>
            <DialogDescription>
              They will receive an email whenever a request enters one of their assigned approval classes.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                         placeholder="e.g. Dr. James Ruto" data-testid="route-name-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input value={editing.email} onChange={(e) => pickUser(e.target.value)}
                         placeholder="email@example.com" list="route-user-suggestions"
                         autoComplete="off" data-testid="route-email-input" />
                  <datalist id="route-user-suggestions">
                    {users.filter((u) => u.email).map((u) => (
                      <option key={u.email} value={u.email}>{u.name}</option>
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Department approvals</Label>
                <div className="grid grid-cols-2 gap-1.5 max-h-44 overflow-y-auto border border-[#E8E8E6] rounded-lg p-2" data-testid="route-dept-checkboxes">
                  {departments.map((d) => {
                    const key = `department:${d.key}`;
                    return (
                      <label key={key} className="flex items-center gap-2 text-sm cursor-pointer px-1 py-0.5 rounded hover:bg-[#F4F4F1]">
                        <input type="checkbox" checked={editing.classes.includes(key)}
                               onChange={() => toggleClass(key)}
                               data-testid={`route-class-${d.key}`} />
                        {d.name || d.key}
                      </label>
                    );
                  })}
                  {departments.length === 0 && <p className="text-[11px] text-[#7A8A84] col-span-2">No departments found.</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Other stages</Label>
                <div className="flex flex-col gap-1.5 border border-[#E8E8E6] rounded-lg p-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer px-1 py-0.5 rounded hover:bg-[#F4F4F1]">
                    <input type="checkbox" checked={editing.classes.includes("cmo")}
                           onChange={() => toggleClass("cmo")} data-testid="route-class-cmo" />
                    Admin / CMO approvals
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer px-1 py-0.5 rounded hover:bg-[#F4F4F1]">
                    <input type="checkbox" checked={editing.classes.includes("housing")}
                           onChange={() => toggleClass("housing")} data-testid="route-class-housing" />
                    Housing approvals
                  </label>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="route-save-btn">
              {saving ? "Saving…" : editing?.id ? "Save changes" : "Add approver"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
